import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEdit, FiTrash2, FiPlus, FiSave, FiX, FiUsers, FiBookOpen, FiTrendingUp, FiEye } = FiIcons;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('teachers');
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Mock data - in real app, this would come from API/database
  const [teachers, setTeachers] = useState([
    {
      id: 1,
      name: 'Sarah Johnson',
      school: 'Pine Valley Elementary',
      location: 'Denver, Colorado',
      subjects: ['Science', 'Math'],
      grades: ['3rd', '4th', '5th'],
      bio: 'Passionate about making STEM accessible to all students through hands-on learning.',
      photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300',
      activeProjects: 3,
      completedProjects: 12,
      totalRaised: 15420,
      joinDate: '2022'
    },
    {
      id: 2,
      name: 'David Chen',
      school: 'Riverside Middle School',
      location: 'San Francisco, California',
      subjects: ['English', 'Literature'],
      grades: ['6th', '7th', '8th'],
      bio: 'Dedicated to fostering a love of reading and creative writing in young minds.',
      photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300',
      activeProjects: 2,
      completedProjects: 8,
      totalRaised: 9870,
      joinDate: '2023'
    }
  ]);

  const [featuredProjects, setFeaturedProjects] = useState([
    {
      id: 1,
      title: 'STEM Lab Equipment for Rural School',
      teacher: 'Ms. Sarah Johnson',
      school: 'Pine Valley Elementary',
      location: 'Colorado',
      goal: 2500,
      raised: 1850,
      image: 'https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?w=400',
      description: 'Help us create an engaging STEM lab with microscopes, circuit kits, and robotics materials.',
      category: 'Science',
      isFeatured: true
    },
    {
      id: 2,
      title: 'Reading Corner Books Collection',
      teacher: 'Mr. David Chen',
      school: 'Riverside Middle School',
      location: 'California',
      goal: 800,
      raised: 650,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      description: 'Building a diverse library collection for students of all reading levels.',
      category: 'Literature',
      isFeatured: true
    }
  ]);

  const handleSaveTeacher = (teacherData) => {
    if (editingTeacher) {
      setTeachers(teachers.map(t => t.id === editingTeacher.id ? { ...teacherData, id: editingTeacher.id } : t));
    } else {
      setTeachers([...teachers, { ...teacherData, id: Date.now() }]);
    }
    setEditingTeacher(null);
    setShowAddForm(false);
  };

  const handleSaveProject = (projectData) => {
    if (editingProject) {
      setFeaturedProjects(featuredProjects.map(p => p.id === editingProject.id ? { ...projectData, id: editingProject.id } : p));
    } else {
      setFeaturedProjects([...featuredProjects, { ...projectData, id: Date.now(), isFeatured: true }]);
    }
    setEditingProject(null);
    setShowAddForm(false);
  };

  const handleDeleteTeacher = (id) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      setTeachers(teachers.filter(t => t.id !== id));
    }
  };

  const handleDeleteProject = (id) => {
    if (window.confirm('Are you sure you want to remove this featured project?')) {
      setFeaturedProjects(featuredProjects.filter(p => p.id !== id));
    }
  };

  const TeacherForm = ({ teacher, onSave, onCancel }) => {
    const [formData, setFormData] = useState(teacher || {
      name: '',
      school: '',
      location: '',
      subjects: [],
      grades: [],
      bio: '',
      photo: '',
      activeProjects: 0,
      completedProjects: 0,
      totalRaised: 0,
      joinDate: new Date().getFullYear().toString()
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          {teacher ? 'Edit Teacher' : 'Add New Teacher'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
              <input
                type="text"
                value={formData.school}
                onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subjects (comma-separated)</label>
            <input
              type="text"
              value={Array.isArray(formData.subjects) ? formData.subjects.join(', ') : ''}
              onChange={(e) => setFormData({ ...formData, subjects: e.target.value.split(', ').filter(s => s.trim()) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Science, Math, English"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Grades (comma-separated)</label>
            <input
              type="text"
              value={Array.isArray(formData.grades) ? formData.grades.join(', ') : ''}
              onChange={(e) => setFormData({ ...formData, grades: e.target.value.split(', ').filter(g => g.trim()) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="3rd, 4th, 5th"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photo URL</label>
            <input
              type="url"
              value={formData.photo}
              onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiSave} />
              <span>Save</span>
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiX} />
              <span>Cancel</span>
            </button>
          </div>
        </form>
      </motion.div>
    );
  };

  const ProjectForm = ({ project, onSave, onCancel }) => {
    const [formData, setFormData] = useState(project || {
      title: '',
      teacher: '',
      school: '',
      location: '',
      goal: 0,
      raised: 0,
      image: '',
      description: '',
      category: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          {project ? 'Edit Featured Project' : 'Add New Featured Project'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teacher</label>
              <input
                type="text"
                value={formData.teacher}
                onChange={(e) => setFormData({ ...formData, teacher: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
              <input
                type="text"
                value={formData.school}
                onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Goal ($)</label>
              <input
                type="number"
                value={formData.goal}
                onChange={(e) => setFormData({ ...formData, goal: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Raised ($)</label>
              <input
                type="number"
                value={formData.raised}
                onChange={(e) => setFormData({ ...formData, raised: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select Category</option>
              <option value="Science">Science</option>
              <option value="Math">Math</option>
              <option value="English">English</option>
              <option value="Art">Art</option>
              <option value="Music">Music</option>
              <option value="Sports">Sports</option>
              <option value="Technology">Technology</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
            <input
              type="url"
              value={formData.image}
              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiSave} />
              <span>Save</span>
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiX} />
              <span>Cancel</span>
            </button>
          </div>
        </form>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage teachers, projects, and platform content</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="text-2xl text-blue-600" />
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{teachers.length}</div>
                <div className="text-gray-600">Teachers</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiBookOpen} className="text-2xl text-green-600" />
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{featuredProjects.length}</div>
                <div className="text-gray-600">Featured Projects</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="text-2xl text-purple-600" />
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  ${teachers.reduce((sum, t) => sum + t.totalRaised, 0).toLocaleString()}
                </div>
                <div className="text-gray-600">Total Raised</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiEye} className="text-2xl text-yellow-600" />
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">94%</div>
                <div className="text-gray-600">Success Rate</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('teachers')}
                className={`py-4 border-b-2 font-medium text-sm ${
                  activeTab === 'teachers'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Manage Teachers
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-4 border-b-2 font-medium text-sm ${
                  activeTab === 'projects'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Featured Projects
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'teachers' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Teachers Management</h2>
                  <button
                    onClick={() => setShowAddForm(activeTab)}
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiPlus} />
                    <span>Add Teacher</span>
                  </button>
                </div>

                {showAddForm === 'teachers' && (
                  <TeacherForm
                    onSave={handleSaveTeacher}
                    onCancel={() => setShowAddForm(false)}
                  />
                )}

                {editingTeacher && (
                  <TeacherForm
                    teacher={editingTeacher}
                    onSave={handleSaveTeacher}
                    onCancel={() => setEditingTeacher(null)}
                  />
                )}

                <div className="grid grid-cols-1 gap-4">
                  {teachers.map((teacher) => (
                    <motion.div
                      key={teacher.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <img
                        src={teacher.photo}
                        alt={teacher.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{teacher.name}</h3>
                        <p className="text-gray-600">{teacher.school} • {teacher.location}</p>
                        <p className="text-sm text-gray-500">
                          {teacher.subjects.join(', ')} • ${teacher.totalRaised.toLocaleString()} raised
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingTeacher(teacher)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <SafeIcon icon={FiEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteTeacher(teacher.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'projects' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Featured Projects Management</h2>
                  <button
                    onClick={() => setShowAddForm(activeTab)}
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiPlus} />
                    <span>Add Project</span>
                  </button>
                </div>

                {showAddForm === 'projects' && (
                  <ProjectForm
                    onSave={handleSaveProject}
                    onCancel={() => setShowAddForm(false)}
                  />
                )}

                {editingProject && (
                  <ProjectForm
                    project={editingProject}
                    onSave={handleSaveProject}
                    onCancel={() => setEditingProject(null)}
                  />
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {featuredProjects.map((project) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                    >
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-2">{project.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">
                          by {project.teacher} • {project.school}
                        </p>
                        <div className="flex justify-between text-sm text-gray-500 mb-3">
                          <span>${project.raised} raised</span>
                          <span>${project.goal} goal</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                          <div
                            className="bg-primary-500 h-2 rounded-full"
                            style={{ width: `${(project.raised / project.goal) * 100}%` }}
                          ></div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingProject(project)}
                            className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
                          >
                            <SafeIcon icon={FiEdit} />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
                          >
                            <SafeIcon icon={FiTrash2} />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;